// The Dayboard mark and wordmark.
//
// Both live here so there is exactly one copy of each. Before the 2026-08-31
// rebrand the mark was a <div> containing the letter "D", re-typed in three
// places with three slightly different sets of classes, and the marketing site
// carried a fourth, completely different logo — so "the logo" was four things
// and changing it meant finding all of them. The real mark landed at the same
// time as this file, and the point of the file is that the next change is one
// edit.
//
// The geometry is the brand asset (Branding/mark-on-light.svg) verbatim: a
// 100x100 viewBox holding a tall stem and two stacked pills, the lower pill
// carrying the accent. Do not re-draw it by eye — copy the rects.

/**
 * Which ground the mark is sitting on. This is not a colour scheme: the accent
 * pill is the brand orange in both, and only the two neutral rects move, which
 * is what keeps the mark recognisably one mark.
 *
 *   onLight  ink rects — for white/paper surfaces
 *   onDark   near-white rects — for the ink shell
 */
type Ground = 'onLight' | 'onDark';

const NEUTRAL: Record<Ground, string> = {
  onLight: '#0F1620', // ink
  onDark: '#FBFAF8', // paper.bright — the mark's own light fill
};

const ACCENT = '#E07638'; // brand-500, in both grounds

export function BrandMark({
  ground = 'onLight',
  className = '',
  size,
}: {
  ground?: Ground;
  className?: string;
  size?: number;
}) {
  const neutral = NEUTRAL[ground];
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      // The mark is decoration wherever it appears: every one of its call sites
      // sits next to the word "Dayboard" in real text, so a label here would
      // have a screen reader say the name twice.
      aria-hidden="true"
      focusable="false"
    >
      <rect x="18" y="18" width="15" height="64" rx="4" fill={neutral} />
      <rect x="39" y="18" width="43" height="28" rx="10" fill={neutral} />
      <rect x="39" y="54" width="43" height="28" rx="10" fill={ACCENT} />
    </svg>
  );
}

/**
 * The wordmark: "day" in ink at 700, "board" in orange at 400.
 *
 * Two spans rather than an image, so it inherits the type size it is placed at
 * and stays selectable text. It needs Archivo to look right — the weight
 * contrast between 700 and 400 is what makes the split read as one word rather
 * than two — but it degrades honestly to the system stack.
 *
 * The orange half is brand-600 on light, not the brand-500 of the asset: 500 on
 * the paper background is 2.68:1, which is under even the 3:1 that large text
 * is allowed. A logotype is technically exempt from WCAG, but "exempt" and
 * "readable" are different claims and only one of them is worth shipping. On
 * ink there is no such problem, so the shell gets the true brand-500.
 */
export function Wordmark({ ground = 'onLight', className = '' }: { ground?: Ground; className?: string }) {
  return (
    <span className={`tracking-tight ${className}`}>
      <span className={`font-bold ${ground === 'onDark' ? 'text-paper-bright' : 'text-ink'}`}>day</span>
      <span className={`font-normal ${ground === 'onDark' ? 'text-brand-500' : 'text-brand-600'}`}>
        board
      </span>
    </span>
  );
}

/**
 * Mark and wordmark together, in the proportion the horizontal lockup uses.
 * This is what almost every call site actually wants.
 */
export function BrandLockup({
  ground = 'onLight',
  className = '',
  markClass = 'h-8 w-8',
  textClass = 'text-lg',
}: {
  ground?: Ground;
  className?: string;
  markClass?: string;
  textClass?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <BrandMark ground={ground} className={`flex-none ${markClass}`} />
      <Wordmark ground={ground} className={textClass} />
    </span>
  );
}
